import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, BACKEND_URL } from "@/lib/api";
import { Search, Download, Loader2, Inbox } from "lucide-react";
import { toast } from "sonner";

const STATUS_OPTS = [
  { v: "all",        l: "All statuses" },
  { v: "accepted",   l: "Accepted" },
  { v: "delivered",  l: "Delivered" },
  { v: "returned",   l: "Returned" },
  { v: "billable",   l: "Billable" },
  { v: "rejected",   l: "Rejected" },
];

const SECTOR_OPTS = [
  { v: "all", l: "All sectors" },
  { v: "mortgages", l: "Mortgages" },
  { v: "business-finance", l: "Business finance" },
  { v: "debt-help", l: "Debt help" },
  { v: "claims-management", l: "Claims management" },
  { v: "life-insurance", l: "Life insurance" },
  { v: "wealth-management", l: "Wealth management" },
  { v: "investor-leads", l: "Investor leads" },
  { v: "commercial-finance", l: "Commercial finance" },
  { v: "property-investment", l: "Property investment" },
  { v: "payday-loans", l: "Payday loans" },
];

const PAGE = 25;

export default function PublisherLeads() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sector, setSector] = useState("all");
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const params = useMemo(() => {
    const p = new URLSearchParams();
    p.set("limit", PAGE);
    p.set("offset", offset);
    if (sector !== "all") p.set("sector", sector);
    if (status !== "all") p.set("status", status);
    if (q.trim()) p.set("q", q.trim());
    if (dateFrom) p.set("date_from", dateFrom);
    if (dateTo) p.set("date_to", dateTo);
    return p;
  }, [sector, status, q, dateFrom, dateTo, offset]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/publisher/leads?${params}`)
      .then((r) => { if (!alive) return; setRows(r.data?.rows || []); setTotal(r.data?.total || 0); })
      .catch(() => { if (alive) toast.error("Failed to load leads"); })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [params]);

  // Reset to first page when filters change (but not when offset changes)
  useEffect(() => { setOffset(0); }, [sector, status, q, dateFrom, dateTo]);

  const exportCsv = async () => {
    try {
      const exportParams = new URLSearchParams();
      if (sector !== "all") exportParams.set("sector", sector);
      if (status !== "all") exportParams.set("status", status);
      if (dateFrom) exportParams.set("date_from", dateFrom);
      if (dateTo) exportParams.set("date_to", dateTo);
      const token = localStorage.getItem("connexis_token");
      const resp = await fetch(`${BACKEND_URL}/api/publisher/leads.csv?${exportParams}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `connexis_my_leads_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error(e?.message || "Export failed");
    }
  };

  const statusTint = (s) => {
    if (s === "accepted" || s === "billable") return "bg-emerald-50 text-emerald-700 border-emerald-200";
    if (s === "delivered") return "bg-sky-50 text-sky-700 border-sky-200";
    if (s === "returned") return "bg-rose-50 text-rose-700 border-rose-200";
    if (s === "rejected") return "bg-slate-100 text-slate-600 border-slate-200";
    return "bg-slate-50 text-slate-700 border-slate-200";
  };

  return (
    <div className="space-y-6" data-testid="publisher-leads-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">My Leads</h1>
        <p className="text-[13px] text-slate-500 mt-1">Every lead you've sent to Connexis — search, filter and export.</p>
      </div>

      {/* Filter bar */}
      <Card className="rounded-xl border-slate-200">
        <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
          <div className="lg:col-span-2 relative">
            <Search className="size-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email, name, city" className="pl-8 h-9 text-[13px]" data-testid="pub-leads-search" />
          </div>
          <select value={sector} onChange={(e) => setSector(e.target.value)} className="h-9 rounded-md border border-slate-200 text-[13px] px-2.5 bg-white" data-testid="pub-leads-sector">
            {SECTOR_OPTS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-9 rounded-md border border-slate-200 text-[13px] px-2.5 bg-white" data-testid="pub-leads-status">
            {STATUS_OPTS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 text-[13px]" />
          <div className="flex gap-2">
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 text-[13px] flex-1" />
            <Button onClick={exportCsv} variant="outline" className="h-9 px-3 rounded-md text-[12.5px]" data-testid="pub-leads-export">
              <Download className="size-3.5 mr-1" /> CSV
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="rounded-xl border-slate-200 overflow-hidden">
        <CardContent className="p-0">
          {loading ? (
            <div className="px-6 py-16 text-center text-slate-500 text-[13px]"><Loader2 className="size-4 animate-spin inline mr-1" />Loading…</div>
          ) : rows.length === 0 ? (
            <div className="px-6 py-16 text-center text-slate-500 text-[13px]">
              <Inbox className="size-5 mx-auto mb-2 text-slate-300" />
              No leads match these filters yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="pub-leads-table">
                <thead className="bg-slate-50/60 border-b border-slate-200 text-left text-[10px] uppercase tracking-widest text-slate-500">
                  <tr>
                    <th className="px-5 py-3 font-medium">Lead</th>
                    <th className="font-medium">Sector</th>
                    <th className="font-medium">Status</th>
                    <th className="font-medium">Source</th>
                    <th className="font-medium">Received</th>
                    <th className="font-medium text-right pr-5">You earned</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/40" data-testid={`pub-lead-${r.id}`}>
                      <td className="px-5 py-3.5">
                        <div className="text-[13px] font-medium text-slate-900">{(r.first_name || "—")} {(r.last_name || "").charAt(0) || ""}.</div>
                        <div className="text-[11px] text-slate-500">{r.city || "—"} · <span className="font-mono">{r.id?.slice(0, 8)}</span></div>
                      </td>
                      <td className="py-3.5 text-[12.5px] text-slate-700 capitalize">{(r.sector || "").replace(/-/g, " ")}</td>
                      <td className="py-3.5">
                        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] capitalize ${statusTint(r.status)}`}>
                          {r.status}{r.disposition ? ` · ${r.disposition}` : ""}
                        </span>
                      </td>
                      <td className="py-3.5 text-[12px] font-mono text-slate-600">{r.source_sub_id || "(none)"}</td>
                      <td className="py-3.5 text-[12px] text-slate-600 whitespace-nowrap">{new Date(r.created_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                      <td className="py-3.5 pr-5 text-right font-mono tabular-nums text-emerald-700 font-medium">£{Number(r.publisher_earning_gbp || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {/* Pagination */}
          {!loading && rows.length > 0 && (
            <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between text-[12px] text-slate-500">
              <div>Showing <span className="text-slate-800 font-medium tabular-nums">{offset + 1}–{Math.min(offset + PAGE, total)}</span> of <span className="text-slate-800 font-medium tabular-nums">{total.toLocaleString()}</span></div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))} className="h-7 text-[12px]">Previous</Button>
                <Button size="sm" variant="outline" disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)} className="h-7 text-[12px]">Next</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
